import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ValidationIssue } from '@verifyloop/core';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastInput {
  kind: ToastKind;
  message: string;
  /** Rendered under the message when the server returned a validation issue list. */
  issues?: readonly ValidationIssue[];
  detail?: string;
  /** ms; errors default to sticky. */
  timeoutMs?: number;
}

export interface Toast extends ToastInput {
  id: number;
}

interface ToastValue {
  toasts: readonly Toast[];
  push: (input: ToastInput) => void;
  success: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
  /** Server error messages are written to be read, shown verbatim. */
  error: (message: string, issues?: readonly ValidationIssue[]) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = nextId.current;
      nextId.current += 1;
      const toast: Toast = { ...input, id };
      setToasts((current) => [...current.slice(-4), toast]);
      const timeout = input.timeoutMs ?? (input.kind === 'error' ? 0 : 6000);
      if (timeout > 0) {
        timers.current.set(id, window.setTimeout(() => dismiss(id), timeout));
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (message, detail) => push({ kind: 'success', message, ...(detail ? { detail } : {}) }),
      info: (message, detail) => push({ kind: 'info', message, ...(detail ? { detail } : {}) }),
      error: (message, issues) =>
        push({ kind: 'error', message, ...(issues && issues.length > 0 ? { issues } : {}) }),
    }),
    [toasts, push, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}